import { useState } from 'react';
import { Save, History } from '../../lib/icons';
import { toast } from 'sonner';
import { getCoursDuJour, setCoursDuJour } from '../../lib/cours';
import { useAuth } from '../../lib/auth';
import { fmt } from '../shared/badges';

const inputCls =
  'h-11 w-full border border-slate-300 bg-slate-50 px-2 text-right font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';

// Saisie rapide quotidienne du Cours de Base : c'est la référence figée sur chaque
// bon de réception « À négocier ».
export const CoursDuJour = () => {
  const { user } = useAuth();
  const [cours, setCours] = useState(getCoursDuJour());
  const [f, setF] = useState(() => {
    const c = getCoursDuJour();
    return { noble: String(c.noble), lampante: String(c.lampante) };
  });
  const [histo, setHisto] = useState(false);
  const admin = user?.role === 'Administrateur';

  const enregistrer = () => {
    const { error, cours: maj } = setCoursDuJour({ noble: f.noble, lampante: f.lampante, par: user?.name });
    if (error) return toast.error(error);
    setCours(maj);
    toast.success(`Cours du jour enregistré — Extra ${fmt(maj.noble, 3)} · Lampante ${fmt(maj.lampante, 3)} TND/kg`);
  };

  return (
    <section data-testid="widget-cours-jour" className="border border-slate-200 border-l-2 border-l-amber-600 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
        <div>
          <p className="font-heading text-base font-bold tracking-tight text-slate-900">Cours de Base du Jour</p>
          <p data-testid="cours-horodatage" className="font-data text-[11px] text-slate-500">
            {cours.le
              ? `Dernière mise à jour : ${String(cours.le).slice(0, 16).replace('T', ' ')}${cours.par ? ` · ${cours.par}` : ''}`
              : 'Jamais saisi — valeurs de référence par défaut'}
          </p>
        </div>
        <button
          type="button"
          data-testid="cours-historique"
          onClick={() => setHisto((v) => !v)}
          className="flex h-9 items-center gap-1.5 border border-slate-300 px-2 text-xs font-bold text-slate-600 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
        >
          <History size={13} /> Historique ({(cours.historique ?? []).length})
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 px-4 py-4 sm:grid-cols-[1fr_1fr_auto]">
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Extra / Vierge (TND/kg)</span>
          <input
            data-testid="cours-noble"
            type="number"
            step="0.001"
            min="0"
            value={f.noble}
            disabled={!admin}
            onChange={(e) => setF({ ...f, noble: e.target.value })}
            className={`${inputCls} mt-1.5 disabled:bg-slate-100`}
          />
        </label>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Lampante (TND/kg)</span>
          <input
            data-testid="cours-lampante"
            type="number"
            step="0.001"
            min="0"
            value={f.lampante}
            disabled={!admin}
            onChange={(e) => setF({ ...f, lampante: e.target.value })}
            className={`${inputCls} mt-1.5 disabled:bg-slate-100`}
          />
        </label>
        <button
          type="button"
          data-testid="cours-enregistrer"
          onClick={enregistrer}
          disabled={!admin}
          className="mt-1.5 flex h-11 items-center justify-center gap-2 self-end bg-slate-900 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700 disabled:opacity-40"
        >
          <Save size={15} /> Enregistrer
        </button>
      </div>
      {!admin && (
        <p className="px-4 pb-3 text-xs text-slate-500">Consultation seule : seul l’Administrateur modifie le cours de base.</p>
      )}

      {histo && (
        <div data-testid="cours-table-historique" className="overflow-x-auto border-t border-slate-200">
          <table className="w-full border-collapse">
            <thead className="bg-slate-50">
              <tr>
                {['Horodatage', 'Auteur', 'Extra / Vierge', 'Lampante'].map((c) => (
                  <th key={c} className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(cours.historique ?? []).length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-xs text-slate-500">Aucune modification enregistrée.</td></tr>
              ) : (
                cours.historique.map((h, i) => (
                  <tr key={`${h.le}-${i}`} data-testid={`cours-histo-${i}`}>
                    <td className="px-3 py-2 font-data text-xs text-slate-600">{String(h.le).slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-3 py-2 text-xs text-slate-700">{h.par || '—'}</td>
                    <td className="tabular px-3 py-2 font-data text-xs">
                      {fmt(h.ancienNoble ?? 0, 3)} → <span className="font-bold">{fmt(h.noble, 3)}</span>
                    </td>
                    <td className="tabular px-3 py-2 font-data text-xs">
                      {fmt(h.ancienLampante ?? 0, 3)} → <span className="font-bold">{fmt(h.lampante, 3)}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
